import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Utensils,
  Coffee,
  Wine,
  Gift,
  Search,
  CheckCircle2,
  AlertCircle,
  Clock,
  Sparkles,
  ShieldCheck,
  RefreshCw,
  Receipt,
  Store,
  Ticket,
  Award,
  Camera,
  Lock,
  KeyRound,
  Mail,
  LogOut,
  ArrowLeft,
  WifiOff,
  Database,
} from 'lucide-react';
import {
  lookupBadgePerks,
  redeemPerkVoucher,
  LookupPerksResponse,
  RedeemPerkResponse,
} from '../lib/api';
import {
  refreshOfflineScannerManifest,
  lookupOfflineBadgePerks,
  redeemOfflinePerkVoucher,
  flushOfflineScannerQueues,
  getQueuedPerkRedemptions,
} from '../lib/offlineScannerCache';
import { getSession, signInWithEmailPassword, signOut } from '../lib/supabase';

function extractBadgeIdentifier(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.includes('/b/')) {
    const afterB = trimmed.split('/b/').pop() || trimmed;
    return afterB.split('?')[0].split('#')[0].trim();
  }
  return trimmed;
}

export function VendorPerksCounterPage() {
  // Shared Kiosk Auth State
  const [adminKey, setAdminKey] = useState<string>(() => {
    if (typeof window === 'undefined') return 'ssr_kiosk_session';
    try {
      return sessionStorage.getItem('iiab_admin_key') || '';
    } catch {
      return '';
    }
  });
  const [isCheckingSession, setIsCheckingSession] = useState<boolean>(!adminKey);
  const [loginMode, setLoginMode] = useState<'pin' | 'email'>('pin');
  const [pinInput, setPinInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSubmittingAuth, setIsSubmittingAuth] = useState(false);

  // Offline Cache & Queue State
  const [isOffline, setIsOffline] = useState<boolean>(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );
  const [simulateOffline, setSimulateOffline] = useState(false);
  const [cachedBadgeCount, setCachedBadgeCount] = useState<number>(0);
  const [queuedPerkCount, setQueuedPerkCount] = useState<number>(0);

  const syncOfflineState = useCallback(async () => {
    if (!adminKey) return;
    if (typeof navigator === 'undefined' || navigator.onLine) {
      await flushOfflineScannerQueues(adminKey);
    }
    const manifest = await refreshOfflineScannerManifest(adminKey);
    if (manifest) {
      setCachedBadgeCount(manifest.entries?.length || 0);
    }
    const queued = await getQueuedPerkRedemptions();
    setQueuedPerkCount(queued.length);
  }, [adminKey]);

  useEffect(() => {
    if (!adminKey) return;
    syncOfflineState();
    const handleOnline = () => {
      setIsOffline(false);
      syncOfflineState();
    };
    const handleOffline = () => {
      setIsOffline(true);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [adminKey, syncOfflineState]);

  // Counter configuration
  const [vendorName, setVendorName] = useState('JWCC Hospitality Services');
  const [counterId, setCounterId] = useState('JWCC_PAV3_LUNCH_A');
  const [staffId] = useState('counter_staff_01');
  const [selectedDate] = useState<string | undefined>(undefined);

  // Search / Scan State
  const [identifierInput, setIdentifierInput] = useState('');
  const [lookupData, setLookupData] = useState<LookupPerksResponse | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Camera QR Scanner State
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isScanningCamera, setIsScanningCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const cameraCooldownRef = useRef(false);

  // Redemption State
  const [lastReceipt, setLastReceipt] = useState<RedeemPerkResponse | null>(null);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [recentCounterRedemptions, setRecentCounterRedemptions] = useState<RedeemPerkResponse[]>([]);

  useEffect(() => {
    let mounted = true;
    async function checkExistingSession() {
      try {
        const storedKey = sessionStorage.getItem('iiab_admin_key');
        if (storedKey) {
          if (mounted) {
            setAdminKey(storedKey);
            setIsCheckingSession(false);
          }
          return;
        }
        const session = await getSession();
        if (session?.access_token && mounted) {
          sessionStorage.setItem('iiab_admin_key', session.access_token);
          setAdminKey(session.access_token);
        }
      } catch {
        // Ignore session errors
      } finally {
        if (mounted) setIsCheckingSession(false);
      }
    }
    checkExistingSession();
    return () => {
      mounted = false;
    };
  }, []);

  // Lookup Mutation
  const lookupMutation = useMutation({
    mutationFn: async (id: string) => {
      if (isOffline || simulateOffline) {
        return (await lookupOfflineBadgePerks(id, selectedDate)) as any as LookupPerksResponse;
      }
      try {
        return await lookupBadgePerks(id, selectedDate, adminKey);
      } catch (err: any) {
        if (err.status === 404 || err.status === 401 || err.status === 403) {
          throw err;
        }
        return (await lookupOfflineBadgePerks(id, selectedDate)) as any as LookupPerksResponse;
      }
    },
    onSuccess: (data) => {
      setLookupData(data);
      setLookupError(null);
      setRedeemError(null);
    },
    onError: (err: any) => {
      setLookupData(null);
      setLookupError(err.message || 'Attendee or badge not found');
    },
  });

  // Redeem Mutation
  const redeemMutation = useMutation({
    mutationFn: async (perkId: string) => {
      if (!lookupData) throw new Error('No attendee selected');
      const identifier = lookupData.attendee.badgeCode || lookupData.attendee.id;
      if (isOffline || simulateOffline) {
        const res = await redeemOfflinePerkVoucher({
          perkId,
          identifier,
          vendorCounterId: counterId,
          vendorName,
          redemptionDate: selectedDate,
        });
        const queued = await getQueuedPerkRedemptions();
        setQueuedPerkCount(queued.length);
        return res as any as RedeemPerkResponse;
      }
      try {
        return await redeemPerkVoucher(
          {
            perkId,
            identifier,
            vendorCounterId: counterId,
            vendorName,
            redeemedByStaffId: staffId,
          },
          adminKey
        );
      } catch (err: any) {
        if (err.status === 400 || err.status === 403 || err.status === 404 || err.status === 409) {
          throw err;
        }
        const res = await redeemOfflinePerkVoucher({
          perkId,
          identifier,
          vendorCounterId: counterId,
          vendorName,
          redemptionDate: selectedDate,
        });
        const queued = await getQueuedPerkRedemptions();
        setQueuedPerkCount(queued.length);
        return res as any as RedeemPerkResponse;
      }
    },
    onSuccess: (receipt) => {
      setLastReceipt(receipt);
      setRedeemError(null);
      setRecentCounterRedemptions((prev) => [receipt, ...prev]);

      // Refresh attendee perks state
      if (lookupData) {
        setLookupData((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            perks: prev.perks.map((p) => {
              if (p.id === receipt.perk.id) {
                const newRedeemed = p.redeemedCount + 1;
                const newRemaining = Math.max(0, p.maxRedemptions - newRedeemed);
                return {
                  ...p,
                  redeemedCount: newRedeemed,
                  remainingRedemptions: newRemaining,
                  status: newRemaining === 0 ? 'already_redeemed' : 'available',
                  lastRedemption: {
                    redeemedAt: receipt.redeemedAt,
                    vendorName: receipt.vendor.vendorName,
                    vendorCounterId: receipt.vendor.vendorCounterId,
                  },
                };
              }
              return p;
            }),
          };
        });
      }
    },
    onError: (err: any) => {
      setRedeemError(err.message || 'Failed to redeem voucher');
    },
  });

  const handleLookup = (eOrText: React.FormEvent | string) => {
    if (typeof eOrText !== 'string') {
      eOrText.preventDefault();
    }
    const rawValue = typeof eOrText === 'string' ? eOrText : identifierInput;
    const normalized = extractBadgeIdentifier(rawValue);
    if (!normalized) return;
    if (typeof eOrText === 'string') {
      setIdentifierInput(normalized);
    }
    setLastReceipt(null);
    lookupMutation.mutate(normalized);
  };

  const startCameraScanner = async () => {
    setCameraError(null);
    setIsCameraOpen(true);
    setTimeout(async () => {
      try {
        if (!html5QrCodeRef.current) {
          html5QrCodeRef.current = new Html5Qrcode('perks-qr-reader-viewport');
        }
        await html5QrCodeRef.current.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 220, height: 220 },
            aspectRatio: 1.0,
          },
          (decodedText) => {
            if (cameraCooldownRef.current) return;
            cameraCooldownRef.current = true;
            handleLookup(decodedText);
            setTimeout(() => {
              cameraCooldownRef.current = false;
            }, 2500);
          },
          () => {
            // Ignore frame scan misses
          }
        );
        setIsScanningCamera(true);
      } catch (err: any) {
        setCameraError(
          err.message ||
            'Camera access unavailable. Please use the USB/NFC hardware scanner input.'
        );
        setIsScanningCamera(false);
      }
    }, 100);
  };

  const stopCameraScanner = async () => {
    if (html5QrCodeRef.current && isScanningCamera) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch {
        // ignore stop error
      }
    }
    setIsScanningCamera(false);
    setIsCameraOpen(false);
  };

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current
          .stop()
          .catch(() => {})
          .finally(() => {
            html5QrCodeRef.current = null;
          });
      }
    };
  }, []);

  const handlePinLogin = (e: React.FormEvent) => {
    e.preventDefault();
    const key = pinInput.trim();
    if (!key) {
      setAuthError('Please enter a valid Scanner PIN or Staff Passkey.');
      return;
    }
    setAuthError(null);
    sessionStorage.setItem('iiab_admin_key', key);
    setAdminKey(key);
    setPinInput('');
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim() || !passwordInput) {
      setAuthError('Please enter both staff email and password.');
      return;
    }
    setIsSubmittingAuth(true);
    setAuthError(null);
    try {
      const { session, error } = await signInWithEmailPassword(
        emailInput.trim(),
        passwordInput
      );
      if (error || !session?.access_token) {
        setAuthError(typeof error === 'string' ? error : (error as any)?.message || 'Invalid staff credentials.');
      } else {
        sessionStorage.setItem('iiab_admin_key', session.access_token);
        setAdminKey(session.access_token);
        setPasswordInput('');
      }
    } catch (err: any) {
      setAuthError(err.message || 'Authentication failed.');
    } finally {
      setIsSubmittingAuth(false);
    }
  };

  const handleSignOut = async () => {
    await stopCameraScanner();
    try {
      sessionStorage.removeItem('iiab_admin_key');
      await signOut();
    } catch {
      // ignore
    }
    setAdminKey('');
  };

  const getPerkIcon = (code: string) => {
    if (code.includes('LUNCH') || code.includes('MEAL')) return Utensils;
    if (code.includes('VIP') || code.includes('LOUNGE') || code.includes('COFFEE')) return Coffee;
    if (code.includes('GALA') || code.includes('DINNER')) return Wine;
    if (code.includes('KIT')) return Gift;
    return Ticket;
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex items-center gap-2.5 text-slate-600 text-sm font-semibold">
          <RefreshCw className="w-4 h-4 animate-spin text-[#0A3D27]" />
          <span>Verifying Perks Counter session...</span>
        </div>
      </div>
    );
  }

  if (!adminKey) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-br from-[#0A3D27] to-[#062B1B] p-6 text-white space-y-2">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#E6C687] text-[#0A3D27] text-[10px] font-black uppercase tracking-wider">
              <Utensils className="w-3 h-3" />
              <span>Pavilion 3 • Hospitality & Dining Terminal</span>
            </div>
            <h1 className="text-xl font-black tracking-tight">
              IIAB 2027 Staff & Perks Kiosk Login
            </h1>
            <p className="text-xs text-white/80 leading-relaxed">
              Authenticate with your Scanner Passkey PIN or Staff Account to unlock the F&B & Lounge Perks Counter.
            </p>
          </div>

          <div className="p-6 space-y-5">
            <div className="grid grid-cols-2 gap-1.5 p-1 bg-slate-100 rounded-xl text-xs font-bold">
              <button
                type="button"
                onClick={() => {
                  setLoginMode('pin');
                  setAuthError(null);
                }}
                className={`py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  loginMode === 'pin'
                    ? 'bg-white text-[#0A3D27] shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <KeyRound className="w-3.5 h-3.5" />
                <span>Scanner / Staff PIN</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setLoginMode('email');
                  setAuthError(null);
                }}
                className={`py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  loginMode === 'email'
                    ? 'bg-white text-[#0A3D27] shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Mail className="w-3.5 h-3.5" />
                <span>Staff Email Login</span>
              </button>
            </div>

            {authError && (
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{authError}</span>
              </div>
            )}

            {loginMode === 'pin' ? (
              <form onSubmit={handlePinLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-slate-700">
                    Scanner Passkey / Counter PIN
                  </label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="password"
                      value={pinInput}
                      onChange={(e) => setPinInput(e.target.value)}
                      placeholder="Enter scanner passkey or staff key..."
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-hidden focus:border-[#0A3D27] focus:bg-white"
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                >
                  <ShieldCheck className="w-4 h-4 text-[#E6C687]" />
                  <span>Unlock Perks Counter Kiosk</span>
                </button>
              </form>
            ) : (
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-slate-700">
                    Staff Email
                  </label>
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="ops@iiabexpo.com"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-hidden focus:border-[#0A3D27] focus:bg-white"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-slate-700">
                    Password
                  </label>
                  <input
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-hidden focus:border-[#0A3D27] focus:bg-white"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmittingAuth}
                  className="w-full py-3 bg-[#0A3D27] hover:bg-[#062B1B] disabled:opacity-50 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                >
                  {isSubmittingAuth ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 text-[#E6C687]" />
                  )}
                  <span>Sign In & Unlock Kiosk</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Compact Kiosk Top Bar */}
        <div className="bg-[#062B1B] text-white rounded-2xl px-4 py-3 flex flex-wrap items-center justify-between gap-3 shadow-sm border border-[#0A3D27]">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E6C687] text-[#0A3D27] text-[10px] font-black uppercase tracking-wider">
              <Utensils className="w-3 h-3" />
              <span>{counterId}</span>
            </span>
            <span className="text-xs sm:text-sm font-black tracking-wide text-[#FAF5EA]">
              IIAB 2027 • F&B & LOUNGE PERKS COUNTER
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-bold border ${
                isOffline || simulateOffline
                  ? 'bg-amber-500/20 border-amber-400/50 text-amber-200'
                  : 'bg-emerald-500/20 border-emerald-400/40 text-emerald-200'
              }`}
            >
              {isOffline || simulateOffline ? (
                <>
                  <WifiOff className="w-3.5 h-3.5 text-amber-300" />
                  <span>
                    Offline Mode ({queuedPerkCount} Queued • {cachedBadgeCount} Cached)
                  </span>
                </>
              ) : (
                <>
                  <Database className="w-3.5 h-3.5 text-[#E6C687]" />
                  <span>
                    Offline Cache ({cachedBadgeCount} Badges
                    {queuedPerkCount > 0 ? ` • ${queuedPerkCount} Pending` : ''})
                  </span>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                const next = !simulateOffline;
                setSimulateOffline(next);
                if (!next) {
                  syncOfflineState();
                }
              }}
              className={`px-2.5 py-1.5 rounded-xl text-[11px] font-bold transition-colors cursor-pointer ${
                simulateOffline
                  ? 'bg-amber-500 text-slate-950'
                  : 'bg-white/10 hover:bg-white/20 text-[#FAF5EA]'
              }`}
            >
              {simulateOffline ? 'Reconnect & Auto-Sync' : 'Test Offline Mode'}
            </button>

            <Link
              to="/admin/operations"
              className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-colors flex items-center gap-1.5"
            >
              <ArrowLeft className="w-3.5 h-3.5 text-[#E6C687]" />
              <span>← Live Ops Console</span>
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="px-3 py-1.5 rounded-xl bg-rose-600/90 hover:bg-rose-600 text-white text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Lock / Sign Out</span>
            </button>
          </div>
        </div>

        {/* Header Ribbon */}
        <div className="bg-[#0A3D27] text-white rounded-3xl p-6 sm:p-8 shadow-md flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-widest uppercase bg-[#E6C687] text-[#0A3D27]">
                Station Terminal
              </span>
              <span className="text-xs text-white/80">Jio World Convention Centre, Pavilion 3</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white flex items-center gap-2.5">
              <Utensils className="w-7 h-7 text-[#E6C687]" />
              Vendor Perks & Dining Counter
            </h1>
            <p className="text-xs sm:text-sm text-white/80 max-w-xl">
              Scan delegate badge QR or NFC tag to validate and instantly claim meal vouchers, gala dinner tickets, and sponsored benefits.
            </p>
          </div>

          {/* Terminal Settings Box */}
          <div className="bg-white/10 backdrop-blur-xs border border-white/20 rounded-2xl p-4 text-xs space-y-3 shrink-0">
            <div className="flex items-center gap-2 font-bold text-[#FAF5EA]">
              <Store className="w-4 h-4 text-[#E6C687]" />
              <span>Counter Settings</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-white/60 block mb-0.5">Counter ID</label>
                <input
                  type="text"
                  value={counterId}
                  onChange={(e) => setCounterId(e.target.value)}
                  className="bg-black/30 border border-white/20 rounded-lg px-2.5 py-1 text-white text-xs w-full focus:outline-hidden"
                />
              </div>
              <div>
                <label className="text-[10px] text-white/60 block mb-0.5">Vendor Name</label>
                <input
                  type="text"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  className="bg-black/30 border border-white/20 rounded-lg px-2.5 py-1 text-white text-xs w-full focus:outline-hidden"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Scan & Lookup Console */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">
              Badge Scanner / Code Input
            </label>
            <div className="flex items-center gap-2">
              {!isCameraOpen ? (
                <button
                  type="button"
                  onClick={startCameraScanner}
                  className="px-3.5 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <Camera className="w-3.5 h-3.5 text-[#E6C687]" />
                  <span>📷 Camera QR Scan</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopCameraScanner}
                  className="px-3.5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <Camera className="w-3.5 h-3.5" />
                  <span>Stop Camera QR Scan</span>
                </button>
              )}
            </div>
          </div>

          {isCameraOpen && (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex flex-col items-center space-y-3">
              <div className="w-full max-w-sm flex items-center justify-between text-xs font-bold text-slate-700">
                <span className="flex items-center gap-1.5">
                  <Camera className="w-3.5 h-3.5 text-[#0A3D27]" />
                  <span>Optical 4×6 Badge QR Scanner</span>
                </span>
                {isScanningCamera && (
                  <span className="flex items-center gap-1.5 text-[10px] font-mono text-[#0A3D27]">
                    <span className="w-2 h-2 rounded-full bg-[#0A3D27] animate-ping" />
                    ACTIVE (2.5s cooldown)
                  </span>
                )}
              </div>
              <div className="relative w-full max-w-xs aspect-square bg-slate-900 rounded-2xl overflow-hidden border border-slate-300 flex items-center justify-center">
                <div id="perks-qr-reader-viewport" className="w-full h-full" />
              </div>
              {cameraError && (
                <p className="text-xs text-rose-600 font-medium text-center max-w-sm">
                  {cameraError}
                </p>
              )}
            </div>
          )}

          <form onSubmit={handleLookup} className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Scan badge QR, tap NFC, or enter code (e.g. IIAB27-DEL-XYZ, email, attendee ID)..."
                  value={identifierInput}
                  onChange={(e) => setIdentifierInput(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-[#0A3D27] focus:bg-white transition-all"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={lookupMutation.isPending || !identifierInput.trim()}
                className="px-6 py-3.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-extrabold text-sm rounded-2xl shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {lookupMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 text-[#E6C687]" />
                    <span>Verify Perks</span>
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Error Message */}
          {lookupError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-3 text-red-800 text-xs sm:text-sm animate-in fade-in">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
              <span>{lookupError}</span>
            </div>
          )}

          {/* Redemption Error Message */}
          {redeemError && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-center gap-3 text-amber-900 text-xs sm:text-sm animate-in fade-in">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              <span>{redeemError}</span>
            </div>
          )}

          {/* Successful Redemption Receipt Alert */}
          {lastReceipt && (
            <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-900 space-y-2 animate-in zoom-in-95">
              <div className="flex items-center gap-2 font-bold text-sm text-[#0A3D27]">
                <CheckCircle2 className="w-5 h-5 text-[#0A3D27]" />
                <span>Voucher Successfully Redeemed!</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs text-slate-700 pt-1">
                <div>
                  <span className="text-[10px] text-slate-500 block">Voucher</span>
                  <span className="font-bold text-slate-900">{lastReceipt.perk.name}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-500 block">Attendee</span>
                  <span className="font-bold text-slate-900">{lastReceipt.attendee.fullName}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-500 block">Unit Value</span>
                  <span className="font-bold text-[#0A3D27]">₹{lastReceipt.perk.unitValueInr}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-500 block">Counter Station</span>
                  <span className="font-semibold text-slate-800">{lastReceipt.vendor.vendorCounterId}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Attendee Details & Perks List */}
        {lookupData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Column 1: Attendee Profile Card */}
            <div className="lg:col-span-1 bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#0A3D27]/10 flex items-center justify-center text-[#0A3D27] font-black text-lg">
                  {lookupData.attendee.fullName.charAt(0)}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-900 text-base truncate">
                    {lookupData.attendee.fullName}
                  </h3>
                  <p className="text-xs text-slate-500 truncate">
                    {lookupData.attendee.companyName || 'Delegate'}
                  </p>
                </div>
              </div>

              <div className="space-y-2.5 text-xs text-slate-600">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Pass Tier:</span>
                  <span className="font-bold text-slate-900 uppercase tracking-wide">
                    {lookupData.attendee.passTier.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Badge Code:</span>
                  <span className="font-mono font-bold text-[#0A3D27]">
                    {lookupData.attendee.badgeCode || 'No QR Credential'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Active Roles:</span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {lookupData.attendee.roles.map((r, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FAF5EA] text-[#785613] border border-[#E6C687]/60 uppercase"
                      >
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2 & 3: Entitled Perks & Vouchers Grid */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#785613]" />
                  Available Vouchers & Entitlements
                </h3>
                <span className="text-xs text-slate-500">
                  Date: <span className="font-semibold text-slate-800">{lookupData.checkDate}</span>
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {lookupData.perks.map((perk) => {
                  const Icon = getPerkIcon(perk.code);
                  const isAvailable = perk.status === 'available';
                  const isRedeemed = perk.status === 'already_redeemed';
                  const isIneligible = perk.status === 'ineligible';

                  return (
                    <div
                      key={perk.id}
                      className={`border rounded-2xl p-5 space-y-4 transition-all ${
                        isAvailable
                          ? 'bg-white border-slate-200 hover:border-[#0A3D27] shadow-xs'
                          : isRedeemed
                          ? 'bg-slate-50 border-slate-200 opacity-80'
                          : 'bg-slate-50/60 border-slate-200/60 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`p-2.5 rounded-xl ${
                              isAvailable
                                ? 'bg-[#0A3D27]/10 text-[#0A3D27]'
                                : 'bg-slate-200 text-slate-500'
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-sm text-slate-900 leading-tight">
                              {perk.name}
                            </h4>
                            <span className="text-[10px] text-slate-500 block">
                              {perk.code} • ₹{perk.unitValueInr}
                            </span>
                          </div>
                        </div>

                        {/* Status Badge */}
                        {isAvailable && (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 shrink-0">
                            Available
                          </span>
                        )}
                        {isRedeemed && (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-200 text-slate-700 shrink-0">
                            Redeemed
                          </span>
                        )}
                        {isIneligible && (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 shrink-0">
                            Ineligible
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-slate-600 line-clamp-2">
                        {perk.description || 'Valid at designated IIAB dining and hospitality pavilions.'}
                      </p>

                      {perk.sponsorName && (
                        <div className="text-[10px] font-semibold text-[#785613] bg-[#FAF5EA] border border-[#E6C687]/40 rounded-lg px-2.5 py-1 flex items-center gap-1.5">
                          <Award className="w-3 h-3 text-[#785613]" />
                          <span>Sponsored by {perk.sponsorName}</span>
                        </div>
                      )}

                      {/* Previous Redemption Info */}
                      {isRedeemed && perk.lastRedemption && (
                        <div className="text-[10px] text-slate-500 bg-slate-100 rounded-lg p-2 space-y-0.5">
                          <div className="font-semibold text-slate-700">Already Redeemed</div>
                          <div>
                            {new Date(perk.lastRedemption.redeemedAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}{' '}
                            at {perk.lastRedemption.vendorCounterId}
                          </div>
                        </div>
                      )}

                      {/* Ineligibility Reason */}
                      {isIneligible && perk.ineligibilityReason && (
                        <div className="text-[10px] text-amber-700 bg-amber-50 rounded-lg p-2">
                          {perk.ineligibilityReason}
                        </div>
                      )}

                      {/* Claim Action Button */}
                      {isAvailable && (
                        <button
                          type="button"
                          onClick={() => redeemMutation.mutate(perk.id)}
                          disabled={redeemMutation.isPending}
                          className="w-full py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-extrabold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                        >
                          <Receipt className="w-3.5 h-3.5 text-[#E6C687]" />
                          <span>
                            {redeemMutation.isPending ? 'Redeeming...' : 'Redeem Voucher'}
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Counter Activity Feed */}
        {recentCounterRedemptions.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-3">
            <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500" />
              Recent Counter Redemptions (This Session)
            </h3>
            <div className="divide-y divide-slate-100 text-xs">
              {recentCounterRedemptions.map((r, i) => (
                <div key={i} className="py-2.5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <span className="font-bold text-slate-900">{r.attendee.fullName}</span>
                    <span className="text-slate-400 mx-1.5">•</span>
                    <span className="text-slate-600">{r.perk.name}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-semibold text-[#0A3D27]">₹{r.perk.unitValueInr}</span>
                    <span className="text-[10px] text-slate-400">
                      {new Date(r.redeemedAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
